import { type ReactNode } from 'react';
import { Badge, Callout, Icon, IconButton, icons, theme } from '@agentisai/design-system';
import type { MemoryEntry } from '@agentisai/shared-types';
import { useAcceptMemory, useDeleteMemory, useMemory } from '../queries/useMemory';

const mutedMessage = {
  padding: theme.space[4],
  color: theme.color.neutral.faint,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

export interface MemoryRailBodyProps {
  /** The selected workstream whose memory to show, or `null` when none is
   * selected — the rail is reachable either way, so the panel explains itself
   * rather than the icon being inert. */
  readonly workstreamId: string | null;
}

/**
 * Memory rail panel body (ADR-0056) — what this workstream has learned about
 * the user, and the place they curate it.
 *
 * Sibling to `FilesRailBody`: same loading / error / empty idiom, same
 * workstream-keyed query. Every entry is AGENT-authored; the user's only two
 * actions are **Keep** (pins the entry and marks it user-confirmed in every
 * agent's prompt) and **Forget** (removes it). There is deliberately no way to
 * write or edit the text: memory lands verbatim in a system prompt, so a
 * free-text field here would be a hand-loaded prompt-injection channel.
 */
export function MemoryRailBody({ workstreamId }: MemoryRailBodyProps) {
  const query = useMemory(workstreamId);
  // Hooks run unconditionally (rules-of-hooks); with no workstream the query is
  // disabled and neither mutation is reachable — there are no rows to act on.
  const accept = useAcceptMemory(workstreamId ?? '');
  const remove = useDeleteMemory(workstreamId ?? '');

  let content: ReactNode;
  if (workstreamId === null) {
    content = (
      <div style={mutedMessage}>
        Select a workstream to see what it has learned. Agents record what matters about you here —
        you keep the ones worth remembering and forget the rest.
      </div>
    );
  } else if (query.isLoading) {
    content = <div style={mutedMessage}>Loading memory…</div>;
  } else if (query.isError) {
    content = (
      <div role="alert" style={mutedMessage}>
        Couldn't load memory. We'll retry automatically.
      </div>
    );
  } else {
    // Newest first (R2 — derived at render). The agent appends chronologically;
    // spread before sorting so we never mutate the React Query cache in place.
    const entries = [...(query.data?.data.memory ?? [])].sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
    );
    content =
      entries.length === 0 ? (
        // The copy deliberately does NOT promise continuous recording: only the
        // agents the Librarian grants `allow_handoff` (discovery + the fallback)
        // carry the typed output proposals ride on, so a workstream routed to a
        // specialist records nothing. Widen the grant before widening the promise.
        <div style={mutedMessage}>
          Nothing learned yet. Agents record what matters about this workstream here — keep the ones
          worth remembering, forget anything they get wrong.
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space[2],
            padding: `0 ${theme.space[4]}`,
          }}
        >
          {entries.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              onAccept={() => accept.mutate(entry.id)}
              onDelete={() => remove.mutate(entry.id)}
              // Scoped to the row actually in flight — a single pending accept
              // must not disable Keep/Forget on every other entry.
              busy={
                (accept.isPending && accept.variables === entry.id) ||
                (remove.isPending && remove.variables === entry.id)
              }
            />
          ))}
        </div>
      );
  }

  return (
    <div>
      {(accept.isError || remove.isError) && (
        <div role="alert" style={{ padding: `${theme.space[3]} ${theme.space[4]} 0` }}>
          <Callout tone="danger">Couldn't save that. Please try again.</Callout>
        </div>
      )}
      {content}
    </div>
  );
}
MemoryRailBody.displayName = 'MemoryRailBody';

/** One memory entry: the fact, who recorded it, and the two actions. */
function EntryRow({
  entry,
  onAccept,
  onDelete,
  busy,
}: {
  readonly entry: MemoryEntry;
  readonly onAccept: () => void;
  readonly onDelete: () => void;
  readonly busy: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space['1_5'],
        padding: theme.space[3],
        borderRadius: theme.radii.lg,
        border: `1px solid ${theme.color.neutral.border}`,
        // `bgRaised` is documented in the token scale as the memory/agent-card
        // surface on the rail panel — this is the surface it was named for.
        background: theme.color.neutral.bgRaised,
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: theme.typography.fontSize.bodySm,
          lineHeight: theme.typography.lineHeight.body,
          color: theme.color.neutral.body,
        }}
      >
        {entry.fact}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.space[2] }}>
        <Badge tone="coordination" variant="compact" size="sm">
          {entry.source}
        </Badge>
        {/* `accepted` is not cosmetic — a kept entry is pinned against the
            store's cap and rendered to the agents as user-confirmed. */}
        {entry.accepted && (
          <Badge tone="info" variant="compact" size="sm">
            Kept
          </Badge>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: theme.space[1] }}>
          {!entry.accepted && (
            <IconButton
              aria-label={`Keep memory: ${entry.fact}`}
              icon={<Icon as={icons.Check} size={14} />}
              size="sm"
              onClick={onAccept}
              disabled={busy}
            />
          )}
          <IconButton
            aria-label={`Forget memory: ${entry.fact}`}
            icon={<Icon as={icons.Trash2} size={14} />}
            size="sm"
            onClick={onDelete}
            disabled={busy}
          />
        </div>
      </div>
    </div>
  );
}
EntryRow.displayName = 'EntryRow';
